import { STALE_TIMES } from '@iheartjane/data-access/react-query';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';

import { getJwt, removeJwt } from '@jane/shared/auth';
import { getJaneDeviceId } from '@jane/shared/util';

import { janeApi } from '../api/janeApi';
import { sendAnalytics } from '../janeDeviceId/janeDeviceId';
import { createUseQueryHook } from '../utils/createUseQueryHook';
import type {
  BrandDiscountStatuses,
  JaneUser,
  UnreviewedProduct,
  UnreviewedProductsProps,
  UserBrandDiscounts,
} from './user.types';
import { userQueryKeys, userUrls } from './user.util';

// Export for testing
const fetchJaneUser = async (): Promise<JaneUser> => {
  const jdid = getJaneDeviceId();

  // Create URLSearchParams object
  const queryParams = new URLSearchParams();

  // Get 'ref' from current URL if it exists
  const currentParams = new URLSearchParams(window.location.search);
  const ref = currentParams.get('ref');

  // Add parameters when they exist
  if (ref) {
    queryParams.set('ref', ref);
  }

  // Convert to string format (empty or with '?' prefix)
  const queryString = queryParams.toString()
    ? `?${queryParams.toString()}`
    : '';

  const jwt = getJwt();

  return await janeApi
    .get<JaneUser>(`${userUrls.janeUser()}${queryString}`, jwt)
    .then((result) => {
      const isAuthenticated = Boolean(result?.user?.id);

      // whoami answers 200 with an empty user when it refuses a token, so a surviving
      // token is dead. Skip if something stored a newer one mid-flight.
      if (jwt && !isAuthenticated && getJwt() === jwt) {
        removeJwt();
      }

      if (isAuthenticated && jdid) {
        sendAnalytics({
          jdid,
          juid: result.user.id,
          source: 'whoami',
        });
      }

      return {
        ...result,
        user: {
          ...result.user,
          authenticated: isAuthenticated ?? false,
        },
      };
    });
};

const fetchUserBrandDiscounts = async (
  status: BrandDiscountStatuses
): Promise<UserBrandDiscounts> => {
  return await janeApi
    .get<UserBrandDiscounts>(userUrls.userBrandDiscounts(status), getJwt())
    .then((result) => result);
};

export const useJaneUser = createUseQueryHook<void, JaneUser>(
  fetchJaneUser,
  userQueryKeys.janeUser
);

export const useUserBrandDiscounts = ({
  enabled = true,
  janeUserId,
  status,
}: {
  enabled?: boolean;
  janeUserId?: string | number;
  status: BrandDiscountStatuses;
}) => {
  return useQuery({
    queryFn: () => fetchUserBrandDiscounts(status),
    queryKey: userQueryKeys.userBrandDiscounts(janeUserId, status),
    enabled: !!janeUserId && Boolean(enabled),
  });
};

export const useJaneUserId = () => {
  const { data: janeUser } = useJaneUser();
  return janeUser?.user?.id;
};

/** Stable empty default: a fresh `[]` would churn every caller's dep arrays. */
const NO_SEGMENTS: number[] = [];

export const useUserSegmentIds = (
  queryOptions: Omit<
    UseQueryOptions<JaneUser, Error>,
    'queryKey' | 'queryFn'
  > = {}
): number[] => {
  const { data: janeUser } = useJaneUser(undefined, queryOptions);
  return janeUser?.user_segments || NO_SEGMENTS;
};

const fetchUnreviewedProducts = async ({
  storeId,
}: {
  storeId: string | number;
}) => {
  return await janeApi
    .get<{
      products: UnreviewedProduct[];
    }>(userUrls.unreviewedProducts(storeId), getJwt())
    .then((result) => {
      return result.products || [];
    });
};

const enableUnreviewedProductsQuery = ({
  userId,
  storeId,
}: UnreviewedProductsProps) => !!userId && !!storeId;

export const useUnreviewedProducts = createUseQueryHook<
  UnreviewedProductsProps,
  UnreviewedProduct[]
>(
  fetchUnreviewedProducts,
  userQueryKeys.unreviewedProducts,
  enableUnreviewedProductsQuery,
  { staleTime: STALE_TIMES.zero }
);
